import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// Types for Import/Export functionality
export interface ImportExportEntity {
  name: string;
  display_name: string;
  description?: string;
  fields_count?: number;
}

export interface EntityField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  options?: string[];
  validation_rules?: any;
}

export interface ImportExportEntitiesResponse {
  entities: ImportExportEntity[];
  total: number;
  page: number;
  limit: number;
}

export interface EntityFieldsResponse {
  fields: EntityField[];
  entity_type: string;
  total_fields: number;
}

export interface CompareEntitiesResponse {
  new_entities: any[];
  existing_entities: any[];
  conflicts: any[];
  summary: {
    total_new: number;
    total_existing: number;
    total_conflicts: number;
  };
}

export interface ExportDataResponse {
  download_url: string;
  file_size: number;
  total_records: number;
  format: string;
}

export interface ImportDataResponse {
  success: boolean;
  imported_count: number;
  failed_count: number;
  errors: any[];
  warnings: any[];
}

class ImportExportApiService {
  static ENDPOINT = 'api/v2/import-export';

  // Get available entities for import/export
  static async getEntities(params?: {
    entity_type?: string;
    search?: string;
    limit?: number;
    skip?: number;
  }): Promise<APIResponseType<ImportExportEntitiesResponse>> {
    try {
      console.log('🔍 ImportExportApiService.getEntities called with params:', params);
      
      const queryParams = new URLSearchParams();
      if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.skip) queryParams.append('skip', params.skip.toString());

      const url = `${this.ENDPOINT}/entities${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.get<ImportExportEntitiesResponse>(url);
      console.log('📦 ImportExportApiService.getEntities response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.getEntities error:', error);
      throw error;
    }
  }

  // Get fields for a specific entity type
  static async getEntityFields(params: {
    entity_type: string;
  }): Promise<APIResponseType<EntityFieldsResponse>> {
    try {
      console.log('🔍 ImportExportApiService.getEntityFields called with params:', params);
      
      const response = await apiService.get<EntityFieldsResponse>(
        `${this.ENDPOINT}/entities/${params.entity_type}/fields`
      );
      
      console.log('📦 ImportExportApiService.getEntityFields response:', response);
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.getEntityFields error:', error);
      throw error;
    }
  }

  // Export data for a specific entity type
  static async exportData(entityType: string, params?: {
    format?: 'zip' | 'csv' | 'json';
    limit?: number;
    filters?: string;
    fields?: string[];
    dealer_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<APIResponseType<Blob>> {
    try {
      console.log('🔄 ImportExportApiService.exportData called:', { entityType, params });
      
      const queryParams = new URLSearchParams();
      if (params?.format) queryParams.append('format', params.format);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.filters) queryParams.append('filters', params.filters);
      if (params?.fields) queryParams.append('fields', params.fields.join(','));
      if (params?.dealer_id) queryParams.append('dealer_id', params.dealer_id);
      if (params?.start_date) queryParams.append('start_date', params.start_date);
      if (params?.end_date) queryParams.append('end_date', params.end_date);

      const url = `${this.ENDPOINT}/export/${entityType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      // Make the request with responseType blob to get the file and filename from headers
      const response = await apiService.get<Blob>(url, { responseType: 'blob' });
      
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.exportData error:', error);
      throw error;
    }
  }

  // Import data from file (ZIP/CSV/JSON)
  static async importData(entityType: string, formData: FormData, params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }): Promise<APIResponseType<ImportDataResponse>> {
    try {
      console.log('📥 ImportExportApiService.importData called:', { entityType, params });
      
      const queryParams = new URLSearchParams();
      if (params?.dealer_id) queryParams.append('dealer_id', params.dealer_id);
      if (params?.validate_only) queryParams.append('validate_only', params.validate_only.toString());
      if (params?.update_existing) queryParams.append('update_existing', params.update_existing.toString());

      const url = `${this.ENDPOINT}/import/${entityType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.post<ImportDataResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('📨 ImportExportApiService.importData response:', response);
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.importData error:', error);
      throw error;
    }
  }

  // Import data from JSON payload (for edited data)
  static async importDataFromJson(entityType: string, jsonData: any[], params?: {
    dealer_id?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }): Promise<APIResponseType<ImportDataResponse>> {
    try {
      console.log('📥 ImportExportApiService.importDataFromJson called:', { entityType, params, dataCount: jsonData.length });
      
      const queryParams = new URLSearchParams();
      if (params?.dealer_id) queryParams.append('dealer_id', params.dealer_id);
      if (params?.validate_only) queryParams.append('validate_only', params.validate_only.toString());
      if (params?.update_existing) queryParams.append('update_existing', params.update_existing.toString());

      const url = `${this.ENDPOINT}/import/${entityType}`;
      
      const payload = {
        data: jsonData
      };
      
      const response = await apiService.post<ImportDataResponse>(url, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('📨 ImportExportApiService.importDataFromJson response:', response);
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.importDataFromJson error:', error);
      throw error;
    }
  }

  // Compare entities from uploaded file with existing data
  static async compareEntities(entityType: string, formData: FormData, params?: {
    dealer_id?: string;
  }): Promise<APIResponseType<CompareEntitiesResponse>> {
    try {
      console.log('🔍 ImportExportApiService.compareEntities called:', { entityType, params });
      
      const queryParams = new URLSearchParams();
      if (params?.dealer_id) queryParams.append('dealer_id', params.dealer_id);

      const url = `${this.ENDPOINT}/compare/${entityType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.post<CompareEntitiesResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('📨 ImportExportApiService.compareEntities response:', response);
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.compareEntities error:', error);
      throw error;
    }
  }

  // Get import/export status
  static async getStatus(jobId: string): Promise<APIResponseType<{
    job_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    result?: any;
    error?: string;
  }>> {
    try {
      const response = await apiService.get(`${this.ENDPOINT}/status/${jobId}`);
      return response;
    } catch (error) {
      console.error('❌ ImportExportApiService.getStatus error:', error);
      throw error;
    }
  }
}

export default ImportExportApiService;
